/*
Egen hantering av vissa delar gällande Blazors SignalR-anslutning klient <-> Server.
- Start av anslutning till servern.
- Hantering för Tappad och återupprättad anslutning.
*/

import { Status } from '../Constants/StatusEnum';

export class BlazorCustomHandler {

    private hasConnection: boolean = false;
    private firsTime: boolean = true;
    private reconnectTimeout: any = null;
    private currentReconnectAttempt: number = 1;
    private modal: HTMLElement | null = null;
    private CUSTOM_RETRY_INTERVAL_MILLISECONDS: number = 800;
    private reconnectRetryModalHTML: string = '<div><span>Försöker återansluta till servern.</span><div class="loading-spinner"></div><a class="clean" id="CancelRetryBlazorReconnectBtn" title="Avbryt">Avbryt</a></div> ';
    private reconnectFailedModalHTML: string = '<div><span>Anslutningen till servern försvann. Försök återupprätta kopplingen eller ladda om sidan.</span><span class="subMessage">Data i formulär försvinner när du laddar om sidan.</span><a class="clean reload" onclick="window.location.reload()" title="Ladda om sidan">Ladda om sidan</a><div class="clear"></div><a class="button-theme-hav hav" id="TryBlazorReconnectBtn" title="Försök återansluta">Försök återansluta</a></div>';

    private readonly DISPLAY_NONE: string = 'none';
    private readonly DISPLAY_BLOCK: string = 'block';
    private readonly EVENT_NAME_CLICK: string = 'click';
    private readonly CancelRetryBlazorReconnectBtnId = 'CancelRetryBlazorReconnectBtn';
    private readonly TryBlazorReconnectBtnId = 'TryBlazorReconnectBtn';
    private readonly LOG_CONNECTION_UP_MESSAGE = 'Blazor connection is up';
    private readonly LOG_CONNECTION_DOWN_MESSAGE = 'Blazor connection is down, trying to reconnect...';
    private readonly MAX_RECONNECT_RETRIES: number = 8;
    private readonly DEFAULT_RETRY_INTERVAL_MILLISECONDS = 1200;
    private readonly DISPLAY_RECONNECT_MODAL_AFTER_AMOUNT_OF_RETRIES: number = 5;

    constructor() {
        this.Initialize();
    }

    //Initiera anslutningen
    private Initialize(): void {
        this.StartBlazor();
    }

    //Starta SignalR-anslutningen till servern
    private StartBlazor(): void {

        (window as any).Blazor.start({
            configureSignalR: (builder: any) => {
                let c = builder.build();
                c.keepAliveIntervalInMilliseconds = 15000; //skicka en keep alive ping var 15e sekund. Lika på klient och server
                builder.build = () => {
                    return c;
                };
            },
            reconnectionOptions: {
                maxRetries: this.MAX_RECONNECT_RETRIES,
                retryIntervalMilliseconds: this.DEFAULT_RETRY_INTERVAL_MILLISECONDS,
            },
            reconnectionHandler: {
                onConnectionDown: (options: any, error?: Error) => this.ConnectionDown(options, error),
                onConnectionUp: () => this.ConnectionUp()
             }
        }).then(() => {
            this.hasConnection = true;
        }).catch(function (e: any) {
            console.error(e);
        });
    }

    //Om klienten har tappat SignalR-anslutningen till servern körs den här metoden automatiskt från Blazor
    private ConnectionDown(options: any, error?: Error) {
        this.hasConnection = false;
        console.log(this.LOG_CONNECTION_DOWN_MESSAGE);
        this.BuildModal(options.dialogId);
        this.StartBlazorReconnect();
    }

    //Om klienten har återfått SignalR-anslutningen.
    private ConnectionUp() {
        this.ClearTimeout();
        this.hasConnection = true;
        this.firsTime = true;
        console.log(this.LOG_CONNECTION_UP_MESSAGE);
        this.Unset();
    }

    private ClearTimeout(): void {
        if (this.reconnectTimeout !== null) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
        }
    }

    private Unset(): void {
        if (this.modal !== null) { 
            window.document.body.removeChild(this.modal);
            this.modal = null;
        }
    }

    private SetFailedModal(): void {
        this.UpdateReconnectModal(this.reconnectFailedModalHTML);
        this.AddManualReconnectListeners();
    }

    private SetRetryModal(): void {
        this.UpdateReconnectModal(this.reconnectRetryModalHTML);
        this.AddManualCancelListeners();
    }

    //Starta försök med att återfå anslutningen till servern.
    private StartBlazorReconnect() {
        if (this.hasConnection === false) {
            this.Reset();
            this.SetRetryModal();
            this.TryReconnect();
        }
    }

    //Avbryter pågående anslutningsförsök och visar modalinformation om att försöka igen eller ladda om sidan
    private CancelBlazorReconnect() {
        this.SetFailedModal();
        this.Reset();
    }

    private BuildModal(elementId: string): void {
        if (this.hasConnection === false) {
            this.modal = this.GetReconnectModal(elementId);
            window.document.body.appendChild(this.modal);
            this.AddManualCancelListeners();
        }
    }

    //Hämtar anslutning-modal baserat på ID.
    private GetReconnectModal(elementId: string): HTMLElement {
        let modal = document.getElementById(elementId);
        
        if (modal === null) {
            let reconnectModal = <HTMLDivElement>(document.createElement('div'));
            reconnectModal.id = elementId;
            reconnectModal.innerHTML = this.reconnectRetryModalHTML;
            
            modal = reconnectModal;
        }

        return modal;
    }

    //Uppdaterar anslutning-modal med aktuell HTML.
    private UpdateReconnectModal(html: string): void {
        
        if (this.modal !== null) {
            this.modal!.innerHTML = html;
        }
    }

    //Försöker återfå anslutningen till servern
    private async TryReconnect() {
        if (this.hasConnection === false) {

            this.reconnectTimeout = setTimeout(() => {

                if (this.hasConnection === false) {
                    if (this.currentReconnectAttempt >= this.DISPLAY_RECONNECT_MODAL_AFTER_AMOUNT_OF_RETRIES) {
                        this.CUSTOM_RETRY_INTERVAL_MILLISECONDS = this.DEFAULT_RETRY_INTERVAL_MILLISECONDS;
                        this.modal!.style.display = "block";
                    }

                    this.Reconnect().then((result: Status) => {
                        if (this.hasConnection === false) {
                            if (result === Status.Failed && this.currentReconnectAttempt < this.MAX_RECONNECT_RETRIES) {
                                this.TryReconnect();
                            }
                            else {
                                this.SetFailedModal();
                            }
                        }
                        else if (result === Status.Success && this.hasConnection === true) {
                            this.TriggerApplicationEventListenersOnSucceededReconnect();
                        }
                    }).catch(function (e: any) {
                        console.error(e);
                    });

                    this.currentReconnectAttempt++;
                }

            }, this.CUSTOM_RETRY_INTERVAL_MILLISECONDS);
        }
    }

    private async Reconnect(): Promise<Status> {
        try {
            if (this.hasConnection === false) {
                // Beskrivning från Microsoft i vad som kan returneras vid reconnect.
                // - true to mean success
                // - false to mean we reached the server, but it rejected the connection (e.g., unknown circuit ID)
                // - exception to mean we didn't reach the server (this can be sync or async)
                const result = await (window as any).Blazor.reconnect();

                if (!result) {
                    return Status.Failed;
                }
            }
            this.hasConnection = true;
            return Status.Success;

        } catch (exception: any) {
            return Status.Failed;
        }
    }

    //Nollställer modalens meddelande för nya anslutningsförsök
    private Reset(): void {
        if (this.hasConnection === false) {
            if (this.modal !== null) {
                this.currentReconnectAttempt = 1;
                this.modal.style.display = this.firsTime ? this.DISPLAY_NONE : this.DISPLAY_BLOCK;
            }

            this.ClearTimeout();
            this.firsTime = false;
        }
    }

    //Lägger på en click-event-lyssnare om man vill försöka själv igen eller avbryta pågående återanslutningsförsök
    private AddManualReconnectListeners(): void {
        if (this.hasConnection === false) {
            //Försök att ansluta på nytt
            var tryReconnectBtn = document.getElementById(this.TryBlazorReconnectBtnId) as HTMLButtonElement;
            tryReconnectBtn.addEventListener(this.EVENT_NAME_CLICK, this.StartBlazorReconnect.bind(this));
        }
    }

    private AddManualCancelListeners(): void {
        if (this.hasConnection === false) {
            //Avbryt pågående anslutnignsförsök
            var cancelReconnectBtn = document.getElementById(this.CancelRetryBlazorReconnectBtnId) as HTMLButtonElement;
            cancelReconnectBtn.addEventListener(this.EVENT_NAME_CLICK, this.CancelBlazorReconnect.bind(this));
        }
    }

    //Trigga de applikations-event som behöver känna till att vi återfått uppkopplingen för upprätthållen funktion
    private TriggerApplicationEventListenersOnSucceededReconnect(): void {
        window.dispatchEvent(new Event("visibilitychange")); //Lyssnare som vi i Blazor-komponenter kan prenumerera på och agera utifrår. Sker via uppsatt event-service på applikationsnivå 
    }

    public IsConnectionUp(): boolean {
        return this.hasConnection;
    }
}